import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  ShieldIcon, BookIcon, TransportIcon, MessageSquareIcon,
  StoreIcon, ChartIcon, SparkleIcon, ArrowRightIcon,
  CheckIcon, ReceiptIcon, WalletIcon, FinanceIcon,
  HealthIcon, GrowthIcon, ClockIcon, DeviceIcon,
  MealIcon, DocumentIcon,
} from "@/components/ui/TriIcons";

const EASE = [0.4, 0, 0.2, 1] as const;
const TEAL = "#7B1416";
const FOREST = "#1C1917";

type Product = {
  id: string;
  name: string;
  tagline: string;
  category: string;
  icon: typeof ShieldIcon;
  color: string;
  gradient: string;
  features: string[];
  metric: { value: string; label: string };
  unique?: string;
};

const products: Product[] = [
  {
    id: "identity", name: "TriSphere Identity", tagline: "Digital identity for every student.", category: "Foundation",
    icon: ShieldIcon, color: FOREST, gradient: "linear-gradient(135deg, #7B1416, #2E7D5B)",
    features: ["Smart student cards with QR & NFC", "Biometric attendance verification", "Role-based access control", "National ID integration", "One identity across transit, cafeteria, library, exams"],
    metric: { value: "100%", label: "Verified enrollment" },
    unique: "One digital identity works across every campus system — transit, cafeteria, library, and exams",
  },
  {
    id: "learn", name: "TriSphere Learn", tagline: "Digital learning, tracked end-to-end.", category: "Learning",
    icon: BookIcon, color: TEAL, gradient: "linear-gradient(135deg, #C4A882, #D4A84B)",
    features: ["Digital assignments & submissions", "Grade tracking & progress reports", "Curriculum-aligned resources", "Teacher feedback loops", "Parent-visible learning records"],
    metric: { value: "50K+", label: "Active learners" },
    unique: "Learning records feed directly into the parent dashboard — no separate app, no separate login",
  },
  {
    id: "transit", name: "TriSphere Transit", tagline: "Every bus. Every route. Every child.", category: "Transport",
    icon: TransportIcon, color: FOREST, gradient: "linear-gradient(135deg, #7B1416, #3A8A6A)",
    features: ["Real-time GPS tracking", "Student boarding verification", "AI route optimization", "Driver behavior monitoring", "Parent ETA notifications", "Geofencing alerts"],
    metric: { value: "3s", label: "GPS update interval" },
    unique: "Geofencing alerts parents the moment their child boards and exits the bus — automatically",
  },
  {
    id: "connect", name: "TriSphere Connect", tagline: "Parent-school messaging, unified.", category: "Communication",
    icon: MessageSquareIcon, color: TEAL, gradient: "linear-gradient(135deg, #C4A882, #E0B85A)",
    features: ["Real-time messaging", "Announcements & broadcasts", "Consent management", "Read receipts", "Multi-channel: app, SMS, WhatsApp"],
    metric: { value: "99.2%", label: "Delivery rate" },
    unique: "Auto-translates messages between English and local languages — bridging the communication gap",
  },
  {
    id: "campus", name: "TriSphere Campus", tagline: "The digital operating system for every moment of school life.", category: "Campus OS",
    icon: MealIcon, color: FOREST, gradient: "linear-gradient(135deg, #7B1416, #4A9A7A)",
    features: ["Living campus dashboard", "Smart student journey", "Intelligent cafeteria POS", "Boarding & dormitory management", "Health intelligence & clinic", "Security operations", "Library management", "Campus commerce & wallets", "Facilities & maintenance", "Clubs & student life"],
    metric: { value: "2,481", label: "Students on campus now" },
    unique: "Cafeteria POS cross-checks student identity, meal entitlement, wallet balance, food allergies, medical restrictions, and parent controls before every purchase — blocks unsafe meals automatically",
  },
  {
    id: "intelligence", name: "TriSphere Intelligence", tagline: "The AI & analytics layer.", category: "Intelligence",
    icon: SparkleIcon, color: TEAL, gradient: "linear-gradient(135deg, #C4A882, #F0C96B)",
    features: ["Revenue forecasting", "Dropout risk prediction", "Attendance forecasting", "Anomaly detection", "Live dashboards", "Custom report builder", "Open REST & GraphQL APIs"],
    metric: { value: "12+", label: "Prediction models" },
    unique: "Predicts which students are at risk of dropping out — weeks before they do — enabling early intervention",
  },
  {
    id: "government", name: "TriSphere Government", tagline: "Compliance, automated.", category: "Compliance",
    icon: DocumentIcon, color: FOREST, gradient: "linear-gradient(135deg, #7B1416, #2A6A52)",
    features: ["Automated compliance reports", "Ministry submissions", "Student census data", "Accreditation tracking", "Audit-ready transaction trail", "Digital signatures"],
    metric: { value: "100%", label: "Compliance rate" },
    unique: "Generates and submits government compliance reports automatically each term — no manual compilation",
  },
  {
    id: "pay", name: "TriSphere Pay", tagline: "Every payment method. One platform.", category: "Payments",
    icon: ReceiptIcon, color: TEAL, gradient: "linear-gradient(135deg, #C4A882, #D4A84B)",
    features: ["Mobile money (MTN, Airtel)", "Card & bank transfers", "Cash reconciliation", "Auto-receipting", "Instant settlement", "Sub-wallets for meals, books, transport", "Double-entry ledger with real-time reconciliation"],
    metric: { value: "6+", label: "Payment methods" },
    unique: "Sub-wallets let parents restrict funds to specific purposes — meals only, books only, transport only",
  },
];

export function ProductEcosystem() {
  const [activeId, setActiveId] = useState("identity");
  const [filter, setFilter] = useState<string>("All");
  const navigate = useNavigate();

  const categories = ["All", ...Array.from(new Set(products.map(p => p.category)))];
  const filtered = filter === "All" ? products : products.filter(p => p.category === filter);
  const active = products.find(p => p.id === activeId)!;

  return (
    <div>
      {/* Category filter — Safari-style segmented control */}
      <div className="mb-10 inline-flex rounded-full border border-black/[0.06] bg-white p-1 shadow-premium">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setFilter(cat)}
            className="rounded-full px-5 py-2 text-sm transition-all"
            style={{
              background: filter === cat ? FOREST : "transparent",
              color: filter === cat ? "#fff" : "#888",
              fontWeight: filter === cat ? 600 : 400,
            }}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Product grid — Apple bento style */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {filtered.map((p, i) => {
          const isActive = p.id === activeId;
          return (
            <motion.button
              key={p.id}
              onClick={() => setActiveId(p.id)}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04, duration: 0.4 }}
              className="group relative overflow-hidden rounded-2xl border text-left transition-all duration-300"
              style={{
                borderColor: isActive ? p.color : "rgba(26,26,26,0.06)",
                background: isActive ? `${p.color}04` : "#fff",
                boxShadow: isActive ? `0 8px 32px ${p.color}12` : "0 1px 4px rgba(26,26,26,0.03)",
              }}
            >
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <div
                    className="grid h-11 w-11 place-items-center rounded-xl transition-all"
                    style={{ background: isActive ? p.gradient : `${p.color}10` }}
                  >
                    <p.icon
                      className="h-5 w-5"
                      accent={isActive ? "#fff" : p.color}
                      style={{ color: isActive ? "#fff" : p.color }}
                    />
                  </div>
                  <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: p.color }}>
                    {p.category}
                  </span>
                </div>
                <h3 className="mt-4 text-sm font-semibold tracking-tight text-[#1C1917]">{p.name}</h3>
                <p className="mt-1 text-xs leading-relaxed text-[#999]">{p.tagline}</p>
              </div>
              <div
                className="absolute bottom-0 left-0 h-[2px] transition-all duration-300"
                style={{ width: isActive ? "100%" : "0%", background: p.color }}
              />
            </motion.button>
          );
        })}
      </div>

      {/* Active product detail panel — Apple product page style */}
      <AnimatePresence mode="wait">
        <motion.div
          key={active.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.35, ease: EASE }}
          className="mt-6 overflow-hidden rounded-3xl border bg-white"
          style={{ borderColor: `${active.color}15`, boxShadow: `0 8px 40px ${active.color}08` }}
        >
          <div className="h-[3px]" style={{ background: `linear-gradient(90deg, ${active.color}, ${active.color}66)` }} />
          <div className="grid gap-8 p-8 lg:grid-cols-[1.2fr_1fr] lg:p-10">
            {/* Left — product info */}
            <div>
              <div className="flex items-center gap-4">
                <div
                  className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl"
                  style={{ background: active.gradient }}
                >
                  <active.icon className="h-8 w-8" accent="#fff" style={{ color: "#fff" }} />
                </div>
                <div>
                  <h3 className="font-display text-3xl font-medium tracking-tight text-[#1C1917]">
                    {active.name}
                  </h3>
                  <p className="mt-1 text-sm text-[#888]">{active.tagline}</p>
                </div>
              </div>

              {active.unique && (
                <div
                  className="mt-6 flex items-start gap-3 rounded-2xl border p-4"
                  style={{ borderColor: `${active.color}20`, background: `${active.color}05` }}
                >
                  <SparkleIcon className="mt-0.5 h-5 w-5 shrink-0" accent={active.color} style={{ color: active.color }} />
                  <p className="text-sm leading-relaxed text-[#1C1917]/80">
                    <span className="font-semibold" style={{ color: active.color }}>Unique to this market: </span>
                    {active.unique}
                  </p>
                </div>
              )}

              <div className="mt-6 grid grid-cols-2 gap-2.5">
                {active.features.map((f, i) => (
                  <motion.div
                    key={f}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05, duration: 0.25 }}
                    className="flex items-center gap-2.5"
                  >
                    <div className="grid h-5 w-5 shrink-0 place-items-center rounded-full" style={{ background: `${active.color}15` }}>
                      <CheckIcon className="h-3 w-3" accent={active.color} style={{ color: active.color }} />
                    </div>
                    <span className="text-sm text-[#1C1917]/80">{f}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Right — metric card + preview */}
            <div className="flex flex-col gap-4">
              <div
                className="rounded-2xl p-6"
                style={{ background: `${active.color}06`, border: `1px solid ${active.color}15` }}
              >
                <div className="font-display text-5xl font-medium tabular-nums" style={{ color: active.color }}>
                  {active.metric.value}
                </div>
                <div className="mt-2 text-xs uppercase tracking-wider text-[#888]">
                  {active.metric.label}
                </div>
              </div>

              {/* Mini animated preview */}
              <div className="flex-1 rounded-2xl border border-black/[0.06] bg-gradient-to-br from-white to-[#F5EFE4] p-6">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium uppercase tracking-wider text-[#999]">Live Preview</span>
                  <motion.div
                    animate={{ opacity: [1, 0.3, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ background: active.color }}
                  />
                </div>
                <div className="mt-4 space-y-2">
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 2, delay: i * 0.4, repeat: Infinity }}
                      className="h-2 rounded-full"
                      style={{ background: `${active.color}20`, width: `${100 - i * 15}%` }}
                    />
                  ))}
                </div>
                <div className="mt-4 flex items-end gap-1.5">
                  {[40, 65, 50, 80, 60, 90, 70].map((h, i) => (
                    <motion.div
                      key={i}
                      initial={{ height: 0 }}
                      animate={{ height: h }}
                      transition={{ delay: i * 0.08, type: "spring", stiffness: 100 }}
                      className="flex-1 rounded-t"
                      style={{ background: `${active.color}30`, height: h }}
                    />
                  ))}
                </div>
              </div>

              <button
                onClick={() => navigate({ to: "/ecosystem/$productId", params: { productId: active.id } })}
                className="flex items-center justify-center gap-2 rounded-xl border px-5 py-3 text-sm font-medium transition-all hover:shadow-sm"
                style={{ borderColor: `${active.color}30`, color: active.color }}
              >
                Explore {active.name.replace("TriSphere ", "")}
                <ArrowRightIcon className="h-4 w-4" accent={active.color} style={{ color: active.color }} />
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
